import { EAssetType, type FindexInvestment, type ILiability, type ISavedDocument } from 'findef'
import { computed, watch, type Ref } from 'vue'
import { useAssetForm } from '../../hooks/useAssetForm'
import { popModal, pushModal } from 'components/modal/store'
import type { UseAssetFormLayout } from '../../hooks/useAssetForm/types'
import { step1Layout } from '../../hooks/useAssetForm/layouts/steps/step1'
import { EFormEvent, Form } from '@/form/useForm'
import Button from 'components/button'
import { step2Layout } from '../../hooks/useAssetForm/layouts/steps/step2'
import HeaderSm from 'components/headers/sm'

export type QuickAddAssetModalProps = {
  assetName?: string
  assetType?: EAssetType
  listed?: boolean
  currentLiability?: Ref<Omit<ILiability, 'user'>>
}

export const pushQuickAddAssetModal = (props: QuickAddAssetModalProps = {}) => {
  const formHook = useAssetForm({
    currentLiability: props.currentLiability
  })

  if (props.currentLiability) {
    watch(
      props.currentLiability,
      () => {
        formHook.form.setFieldValue('hasLiabilitiesAttached', true)
      },
      { immediate: true }
    )
  }

  if (props.assetName) {
    formHook.form.setFieldValue('asset.name', props.assetName)
  }

  formHook.form.setFieldValue('asset.type', props.assetType || EAssetType.EQUITY)
  formHook.form.setFieldValue('asset.listed', props.listed ?? false);

  if (props.assetType) {
    formHook.form.setFieldValue('selectedAssetType', {
      assetType: props.assetType,
      listed: !!props.listed
    });
  }

  formHook.form.onFieldEvent('selectedAssetType', EFormEvent.FIELD_VALUE_CHANGE, (ev) => {
    const kind = ev.field.value?.value;
    if (kind) {
      formHook.form.setFieldValue('asset.type', kind.assetType);
      formHook.form.setFieldValue('asset.listed', !!kind.listed);
    }
  })

  return new Promise<ISavedDocument<FindexInvestment, string> | null>((resolve) => {
    const handleCancel = () => {
      popModal()
      resolve(null)
    }

    const handleAdd = async () => {
      try {
        const inv = await formHook.save();
        if (inv) {
          resolve(inv);
          popModal();
        }
      } catch (e) {
        console.error(e)
      }
    }

    const layout: UseAssetFormLayout = {
      attributes: {
        class: 'grid grid-cols-1 gap-8 md:gap-12 border border border-gray-300 p-4 md:p-8'
      },
      children: [
        {
          key: 'quick-add-select-asset-type',
          children: [
            {

              key: 'quick-add-select-asset-type-header',
              component: () => () => <HeaderSm title={"Asset Class"} subtitle={"Select what asset class you want to add"} />,
            },
            {

              key: 'quick-add-select-asset-type-selectedAssetType',
              render: 'selectedAssetType'
            }
          ]
        },
        {
          key: 'quick-add-step1Layout-wrapper',
          children: [
            step1Layout
          ]
        },
        {
          title: 'Investment Information',
          description: 'Information you can choose to add now or later.',
          collapsible: true,
          key: 'quick-add-step2Layout-wrapper',
          children: [
            step2Layout
          ]
        }
      ]
    }

    const isLoading = computed(() => formHook.isSaving.value || false)

    const saveIsDisabled = computed(() => {
      return formHook.form.errors.value.length > 0 || isLoading.value
    });

    setTimeout(() => {
      formHook.form.updateErrors()
      formHook.form.updateAllFieldErrors()
      formHook.form.updateErrorList()
    }, 300)

    pushModal({
      title: 'Quick Add Asset',
      body: () => {
        return (
          <div class="space-y-4">
            <Form form={formHook.form} layout={layout} />
          </div>
        )
      },
      actions: computed(() => {
        return (
          <div class="grid grid-cols-[max-content_max-content] items-center justify-end gap-4">
            <Button variant="transparent" outlined onClick={handleCancel}>
              Cancel
            </Button>
            <Button isLoading={isLoading} disabled={saveIsDisabled.value} onClick={handleAdd}>
              Create
            </Button>
          </div>
        )
      }),
      onClose: () => {
        resolve(null)
      },
      style: {
        maxWidth: '820px',
        maxHeight: 'clamp(300px, 70svh, 90svh)',
        minWidth: 'unset',
        width: 'unset'
      }
    })
  })
}
